type PriceObj = { value?: number; currency?: string } | undefined
type PriceRange = { minimum_price?: { final_price?: PriceObj } } | undefined
type ImageLike = { url?: string } | undefined

type CategoryBreadcrumb = { category_name?: string } | null | undefined
type Category =
    | { name?: string; breadcrumbs?: CategoryBreadcrumb[] | null }
    | null
    | undefined

const collectCategories = (cats: Category[]) => {
    const out: string[] = []
    for (const c of cats || []) {
        if (!c) continue
        if (Array.isArray(c.breadcrumbs)) {
            for (const b of c.breadcrumbs)
                if (b?.category_name) out.push(b.category_name)
        }
        if (c.name) out.push(c.name)
    }
    return Array.from(new Set(out.filter(Boolean)))
}

type MinimalProduct = {
    id?: number | string
    name?: string
    sku?: string
    url_key?: string
    categories?: Category[]
    thumbnail?: ImageLike
    small_image?: ImageLike
    price_range?: PriceRange
}

type MinimalVariant = {
    id?: number | string
    sku?: string
    name?: string
    thumbnail?: ImageLike
    price_range?: PriceRange
}

export type AddToCartLineItem = {
    sku: string
    title: string
    productId: number | string
    simpleProductId?: number | string
    categories: string[]
    price: number
    quantity: number
    url: string
    imageUrl?: string
}

type BuildFromProductVariantArgs = {
    product: MinimalProduct
    variant?: MinimalVariant | null
    quantity: number
    url: string
    unitPriceOverride?: number
    imageUrlOverride?: string
}

const toPath = (u?: string) => {
    if (!u) return ''
    try {
        return u.startsWith('http') ? new URL(u).pathname : u
    } catch {
        return u
    }
}
const uniqJoin = (arr: string[]) =>
    Array.from(new Set(arr.filter(Boolean))).join(', ')
const toMoneyNum = (n: number) => Number((n ?? 0).toFixed(2))

export function buildAddToCartItemFromProductVariant({
    product,
    variant,
    quantity,
    url,
    unitPriceOverride,
    imageUrlOverride
}: BuildFromProductVariantArgs): AddToCartLineItem {
    const price =
        unitPriceOverride ??
        variant?.price_range?.minimum_price?.final_price?.value ??
        product?.price_range?.minimum_price?.final_price?.value ??
        0

    const sku = variant?.sku || product?.sku || ''
    const title = variant?.name || product?.name || sku
    const productId = (product?.id as any) ?? ''
    const simpleProductId = (variant?.id as any) || undefined
    const categories = collectCategories(product?.categories || [])
    const imageUrl =
        imageUrlOverride ||
        variant?.thumbnail?.url ||
        product?.thumbnail?.url ||
        product?.small_image?.url

    return {
        sku,
        title,
        productId,
        simpleProductId,
        categories,
        price: Number(price || 0),
        quantity: Number(quantity || 0),
        url,
        imageUrl
    }
}

function buildAddToCartLegacyProps(
    items: AddToCartLineItem[],
    opts?: {
        addedItemIndex?: number
        storeId?: number | string
        maskedQuoteId?: string
        stringifyValue?: boolean
    }
) {
    const addedIdx = Math.max(
        0,
        Math.min(opts?.addedItemIndex ?? 0, Math.max(0, items.length - 1))
    )
    const main = items[addedIdx]
    const valueNum = toMoneyNum(
        items.reduce((s, it) => s + (it.price || 0) * (it.quantity || 0), 0)
    )
    const Value = opts?.stringifyValue ? valueNum.toFixed(2) : valueNum

    const ItemNames = items
        .map((i) => i.title)
        .filter(Boolean)
        .join(', ')
    const Items = items.map((i) => ({
        Categories: i.categories,
        ImageUrlKey: toPath(i.imageUrl),
        ProductId:
            typeof i.productId === 'string' ? Number(i.productId) : i.productId,
        SimpleProductId: String(i.simpleProductId ?? i.productId),
        Price: i.price,
        Title: i.title,
        Url: i.url,
        Quantity: i.quantity
    }))
    const ItemCount = items.length
    const Categories = uniqJoin(items.flatMap((i) => i.categories))

    const props: Record<string, any> = {
        'Added To Cart Value': Value,
        ItemNames,
        Items,
        ItemCount,
        Categories,
        AddedItemCategories: uniqJoin(main?.categories || []),
        AddedItemImageUrlKey: toPath(main?.imageUrl),
        AddedItemPrice: main?.price ?? 0,
        AddedItemQuantity: main?.quantity ?? 0,
        AddedItemProductID:
            typeof main?.productId === 'string'
                ? Number(main?.productId)
                : main?.productId ?? '',
        AddedItemSimpleProductID: String(
            main?.simpleProductId ?? main?.productId ?? ''
        ),
        AddedItemProductName: main?.title ?? '',
        AddedItemSku: main?.sku ?? '',
        AddedItemUrl: main?.url ?? '',
        ...(opts?.storeId != null ? { StoreId: opts.storeId } : {}),
        ...(opts?.maskedQuoteId ? { MaskedQuoteId: opts.maskedQuoteId } : {}),
        $value: Value
    }

    return { eventName: 'Added To Cart' as const, props }
}

export function trackAddedToCartFromItems(
    items: AddToCartLineItem[],
    opts?: Parameters<typeof buildAddToCartLegacyProps>[1] & { email?: string }
) {
    const host = window.location.hostname
    if (host === 'localhost' || host === '127.0.0.1') return false
    const { eventName, props } = buildAddToCartLegacyProps(items, opts)
    if (typeof window === 'undefined') return false
    try {
        const q: any[] = ((window as any).klaviyo =
            (window as any).klaviyo || [])
        if (opts?.email) q.push(['identify', { email: opts.email }])
        q.push(['track', eventName, props])
        return true
    } catch {
        return false
    }
}
